/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { useSettings } from '@/data/Settings';
import { DEFAULT_LOCALE } from '@/data/config/DEFAULTS';
import { constructNextUrl } from '@/data/utils/constructNextUrl';
import { extractParamsOfConcern } from '@/data/utils/extractParamsOfConcern';
import { stripBreadcrumbQuery } from '@/data/utils/stripBreadcrumbQuery';
import { switchOnMock } from '@/data/utils/switchOnMock';
// this is the only place importing useRouter from 'next/router'
// eslint-disable-next-line no-restricted-imports
import { NextRouter, useRouter } from 'next/router';
export { constructNextUrl, extractParamsOfConcern };

export const useNextRouter = () => {
	const _router = useRouter();
	const {
		settings: { storeToken },
	} = useSettings();
	const router = new Proxy(_router, {
		get: (target, prop: keyof NextRouter) => {
			if ((prop === 'push' || prop === 'replace') && typeof target[prop] === 'function') {
				const asPath = target['asPath'];
				return new Proxy(target[prop], {
					apply: (target, thisArg, argumentsList) => {
						const href = argumentsList[0];
						const hrefType = typeof href;
						argumentsList[0] = constructNextUrl(asPath, href, storeToken);

						if (argumentsList[1]) {
							// use explicitly specified as URL as-is with some adjustment for known parameters
							argumentsList[1] = constructNextUrl(asPath, argumentsList[1], storeToken);
						} else if (hrefType === 'object' && typeof href?.query === 'object') {
							// otherwise, we should clone the href without `trail` if it has it
							argumentsList[1] = stripBreadcrumbQuery(argumentsList[0]);
						}

						return Reflect.apply(target, thisArg, argumentsList);
					},
				});
			} else if (prop === 'locale') {
				return switchOnMock({ value: target[prop], mockValue: DEFAULT_LOCALE });
			} else {
				return Reflect.get(target, prop);
			}
		},
	});
	return router;
};
